import React, {
  Suspense,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { frontendTraceManager } from '@/telemetry/FrontendTraceManager';
import { Navigate, Route, Routes, useRoutes } from "react-router-dom";
import routes from "tempo-routes";
import { AuthProvider, useAuth } from "../supabase/auth";
import {
  getOrganizationId,
  supabase,
  switchOrganization,
} from "../supabase/supabase";
import ClientLoginForm from "./components/auth/ClientLoginForm";
import ClientSignUpForm from "./components/auth/ClientSignUpForm";
import InviteAcceptPage from "./components/auth/InviteAcceptPage";
import LoginForm from "./components/auth/LoginForm";
import OrganizationNamePopup from "./components/auth/OrganizationNamePopup";
import SignUpForm from "./components/auth/SignUpForm";
import { ThemeProvider } from "./components/theme/theme-provider";

import { CampaignDetail } from "./components/campaigns/CampaignDetail";
import DashboardLayout from "./components/dashboard/layout/DashboardLayout";
import { FeatureGuard } from "./components/dashboard/layout/FeatureGuard";
import AnalyticsPage from "./components/pages/analytics";
import AssistantDashboardPage from "./components/pages/assistant-dashboard";
import AssistantsPage from "./components/pages/assistants";
import CallLogsPage from "./components/pages/call-logs";
import CallRequestsPage from "./components/pages/call-requests";
import Dashboard from "./components/pages/dashboard";
import FeatureTemplateDetailPage from "./components/pages/feature-template-detail";
import FeatureTemplatesPage from "./components/pages/feature-templates";
import Home from "./components/pages/home";
import LeadUploadsPage from "./components/pages/lead-uploads";
import LeadsPage from "./components/pages/leads";
import MicroPromptsPage from "./components/pages/micro-prompts";
import OrganizationFeaturesPage from "./components/pages/organization-features";
import OrganizationSettingsPage from "./components/pages/organization-settings";
import PersonasPage from "./components/pages/personas";
import SimulatorsPage from "./components/pages/simulators";
import PhoneNumbersPage from "./components/pages/phone-numbers";
import PromptEnginePage from "./components/pages/prompt-engine";
import ProvidersPage from "./components/pages/providers";
import RunMigrationsPage from "./components/pages/RunMigrations";
import SchemaTemplatesPage from "./components/pages/schema-templates";
import SlotDetailPage from "./components/pages/slot-detail";
import SlotsPage from "./components/pages/slots";
import Success from "./components/pages/success";
import SuperAdminPage from "./components/pages/superadmin";
import UseCaseTemplatePage from "./components/pages/use-case-template";
import CustomerSupportPage from "./components/pages/use-cases/customer-support";
import DispatchServicePage from "./components/pages/use-cases/dispatch-service";
import LeadQualificationPage from "./components/pages/use-cases/lead-qualification";
import ReceptionistsPage from "./components/pages/use-cases/receptionists";
import VideoCallPage from "./components/pages/VideoCallPage";
import { InterviewComplete } from "./components/video-call/InterviewComplete";
import DemoVideoCallPage from "./components/pages/DemoVideoCallPage";
import { PreflightTestPage } from "./components/pages/PreflightTestPage";
import VideoCallPreviewPage from "./components/pages/VideoCallPreviewPage";
import VoiceAgentsPage from "./components/pages/voice-agents";
import SiriWaveTestPage from "./components/pages/siriwave-test";
import ScheduledCallsPage from "./components/pages/scheduled-calls";
import CallFlowsPage from "./components/pages/callflows";
import CallFlowDetailPage from "./components/pages/callflow-detail";
import { VideoCallTelemetryTest } from "./components/telemetry/VideoCallTelemetryTest";
import { CallLatencyDashboard } from "./components/analytics/CallLatencyDashboard";
import { OverallLatencyDashboard } from "./components/analytics/OverallLatencyDashboard";
import { CreditsBalanceDashboard } from "./components/credits/CreditsBalanceDashboard";
import HealthCheck from "./components/HealthCheck";
import DirectPromptGenerator from "./components/prompt-engine/DirectPromptGenerator";
import PromptAIEdit from "./components/prompt-engine/PromptAIEdit";
import PromptDetail from "./components/prompt-engine/PromptDetail";
import PromptEdit from "./components/prompt-engine/PromptEdit";
import PromptGenerator from "./components/prompt-engine/PromptGenerator";
import PromptHistory from "./components/prompt-engine/PromptHistory";
import TranscriptDetail from "./components/prompt-engine/TranscriptDetail";
import TranscriptInput from "./components/prompt-engine/TranscriptInput";
import PublicSlotBooking from "./components/slots/PublicSlotBooking";
import { LoadingScreen } from "./components/ui/loading-spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Toaster } from "./components/ui/toaster";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { TelemetryDebug } from "./components/debug/TelemetryDebug";
import * as Sentry from "@sentry/react";

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen text="Authenticating..." />;
  }

  if (!user) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
}

const AppRoutes = () => {
  const tempoRoutes =
    import.meta.env.VITE_TEMPO === "true" ? useRoutes(routes) : null;
  const { currentOrganization } = useOrganization();

  return (
    <>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<LoginForm />} />
        <Route path="/signup" element={<SignUpForm />} />
        <Route path="/invite-accept" element={<InviteAcceptPage />} />
        <Route path="/client/login" element={<ClientLoginForm />} />
        <Route path="/client/signup" element={<ClientSignUpForm />} />
        <Route
          path="/use-cases/lead-qualification"
          element={<LeadQualificationPage />}
        />
        <Route
          path="/use-cases/customer-support"
          element={<CustomerSupportPage />}
        />
        <Route
          path="/use-cases/receptionists"
          element={<ReceptionistsPage />}
        />
        <Route
          path="/use-cases/dispatch-service"
          element={<DispatchServicePage />}
        />
        <Route path="/book-slot/:id" element={<PublicSlotBooking />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Sentry.ErrorBoundary fallback={<div>Something went wrong with the dashboard</div>}>
                <Dashboard />
              </Sentry.ErrorBoundary>
            </PrivateRoute>
          }
        />
        <Route path="/success" element={<Success />} />
        <Route path="/health-check" element={<HealthCheck />} />
        <Route path="/debug-telemetry" element={<TelemetryDebug />} />
        <Route
          path="/assistant-dashboard"
          element={<Navigate to="/assistants" replace />}
        />
        <Route
          path="/agent-dashboard"
          element={<Navigate to="/assistants" replace />}
        />
        <Route
          path="/organization-settings/:organizationId?"
          element={
            <PrivateRoute>
              <OrganizationSettingsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/credits-balance"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Credits Balance"
                title="Credits Balance"
                description="View provider-reported balances where supported"
              >
                <CreditsBalanceDashboard />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/assistants/*"
          element={
            <PrivateRoute>
              <Sentry.ErrorBoundary fallback={<div>Something went wrong with assistants</div>}>
                <AssistantsPage />
              </Sentry.ErrorBoundary>
            </PrivateRoute>
          }
        />
        {/* <Route
          path="/assistants/:id"
          element={
            <PrivateRoute>
              <AssistantsPage />
            </PrivateRoute>
          }
        /> */}
        <Route
          path="/personas"
          element={
            <PrivateRoute>
              <PersonasPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/simulators"
          element={
            <PrivateRoute>
              <SimulatorsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin"
          element={
            <PrivateRoute>
              <SuperAdminPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/organization-features/:id"
          element={
            <PrivateRoute>
              <OrganizationFeaturesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/providers"
          element={
            <PrivateRoute>
              <FeatureGuard featureId="providers">
                <ProvidersPage />
              </FeatureGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/phone-numbers"
          element={
            <PrivateRoute>
              <PhoneNumbersPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/call-requests"
          element={
            <PrivateRoute>
              <CallRequestsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/call-logs"
          element={
            <PrivateRoute>
              <CallLogsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/scheduled-calls"
          element={
            <PrivateRoute>
              <ScheduledCallsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/callflows"
          element={
            <PrivateRoute>
              <CallFlowsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/callflows/:id"
          element={
            <PrivateRoute>
              <CallFlowDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/call/:callId/latency"
          element={
            <PrivateRoute>
              <CallLatencyDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/analytics/latency"
          element={
            <PrivateRoute>
              <OverallLatencyDashboard />
            </PrivateRoute>
          }
        />
        <Route path="/voice-agents" element={<VoiceAgentsPage />} />
        <Route path="/siriwave-test" element={<SiriWaveTestPage />} />
        <Route path="/telemetry-test" element={<VideoCallTelemetryTest />} />
        <Route path="/video-call-preview" element={<VideoCallPreviewPage />} />
        <Route
          path="/run-migrations"
          element={
            <PrivateRoute>
              <RunMigrationsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/prompt-engine"
          element={
            <PrivateRoute>
              <PromptEnginePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/micro-prompts"
          element={
            <PrivateRoute>
              <MicroPromptsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/prompt-engine/transcript/create"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Prompt Engine"
                title="Add New Transcript"
                description="Add a conversation transcript to use for prompt generation"
              >
                <TranscriptInput />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/prompt-engine/create"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Prompt Engine"
                title="Generate Prompt"
                description="Create a new AI prompt directly or based on a transcript"
              >
                <Tabs defaultValue="direct" className="w-full mb-6">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="direct">Direct Input</TabsTrigger>
                    <TabsTrigger value="transcript">
                      From Transcript
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="direct" className="mt-4">
                    <DirectPromptGenerator />
                  </TabsContent>
                  <TabsContent value="transcript" className="mt-4">
                    <PromptGenerator />
                  </TabsContent>
                </Tabs>
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/prompt-engine/prompt/:id"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Prompt Engine"
                title="Prompt Details"
                description="View prompt details and content"
              >
                <PromptDetail />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/prompt-engine/history/:id"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Prompt Engine"
                title="Prompt History"
                description="View and compare prompt versions"
              >
                <PromptHistory />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/prompt-engine/edit/:id"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Prompt Engine"
                title="Edit Prompt"
                description="Make changes to your prompt"
              >
                <PromptEdit />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/prompt-engine/ai-edit/:id"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Prompt Engine"
                title="AI-Assisted Prompt Editing"
                description="Improve your prompt with AI assistance"
              >
                <PromptAIEdit />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/prompt-engine/transcript/:id"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Prompt Engine"
                title="Transcript Details"
                description="View transcript content"
              >
                <TranscriptDetail />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/schema-templates"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Schema Templates"
                title="Schema Templates"
                description="Manage your JSON schema templates"
              >
                <SchemaTemplatesPage />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/slots"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Slots"
                title="Slot Management"
                description="Create and manage time slots with capacity"
              >
                <SlotsPage />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/slots/:id"
          element={
            <PrivateRoute>
              <SlotDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/lead-uploads"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Lead Uploads"
                title="Lead Uploads"
                description="Upload and manage lead files for data extraction"
              >
                <LeadUploadsPage />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/leads"
          element={
            <PrivateRoute>
              <LeadsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/video-call/:id"
          element={
            <ErrorBoundary>
              <VideoCallPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="/interview-complete"
          element={
            <ErrorBoundary>
              <InterviewComplete />
            </ErrorBoundary>
          }
        />
        <Route
          path="/preflight-test"
          element={
            <ErrorBoundary>
              <PreflightTestPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="/demo-video-call"
          element={
            <PrivateRoute>
              <DemoVideoCallPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <PrivateRoute>
              <AnalyticsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/feature-templates"
          element={
            <PrivateRoute>
              <FeatureTemplatesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/feature-templates/:id"
          element={
            <PrivateRoute>
              <FeatureTemplateDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/features"
          element={
            <PrivateRoute>
              <DashboardLayout
                activeItem="Features"
                title="Organization Features"
                description="Manage feature flags for your organization"
              >
                {currentOrganization ? (
                  <Navigate
                    to={`/organization-features/${currentOrganization.id}`}
                    replace
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <h2 className="text-lg font-medium">
                        No Organization Selected
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        Please select an organization to manage features.
                      </p>
                    </div>
                  </div>
                )}
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/u/:templateName"
          element={
            <PrivateRoute>
              <DashboardLayout>
                <UseCaseTemplatePage />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/u/:templateName/:id"
          element={
            <PrivateRoute>
              <DashboardLayout>
                <CampaignDetail />
              </DashboardLayout>
            </PrivateRoute>
          }
        />
      </Routes>
      {tempoRoutes}
    </>
  );
};

function OrganizationPopupHandler() {
  const { showOrganizationPopup, createOrganization } = useAuth();

  return (
    <OrganizationNamePopup
      open={showOrganizationPopup}
      onSubmit={createOrganization}
    />
  );
}

type Organization = {
  id: string;
  name: string;
  is_pro?: boolean;
  api_key?: boolean;
  feature_flags?: Record<string, boolean>;
};

type OrganizationContextType = {
  currentOrganization: Organization | null;
  setCurrentOrganization: (org: Organization | null) => void;
  organizations: Organization[];
  loadOrganizations: () => Promise<void>;
  isLoading: boolean;
};

const OrganizationContext = createContext<OrganizationContextType>({
  currentOrganization: null,
  setCurrentOrganization: () => {},
  organizations: [],
  loadOrganizations: async () => {},
  isLoading: false,
});

// Define the organization hook as a named export
export function useOrganization() {
  return useContext(OrganizationContext);
}

// Define the organization provider as a named export
export function OrganizationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [currentOrganization, setCurrentOrganization] =
    useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  // Initialize the single parent trace for entire frontend session
  useEffect(() => {
    console.log('🌟 Initializing frontend parent trace from OrganizationProvider');
    frontendTraceManager.initializeSession({
      userId: user?.id,
      organizationId: currentOrganization?.id,
    });

    // End session on component unmount
    return () => {
      frontendTraceManager.endSession('app_unmount');
    };
  }, []);

  // Update session when user/org changes
  useEffect(() => {
    if (user || currentOrganization) {
      frontendTraceManager.updateSession({
        userId: user?.id,
        organizationId: currentOrganization?.id,
      });
    }
  }, [user?.id, currentOrganization?.id]);

  const loadOrganizations = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Get the active organization ID from JWT
      const activeOrgId = await getOrganizationId();

      // Load organizations the user is a member of by joining with organization_members
      const { data: orgsData, error: orgsError } = await supabase
        .from("organization_members")
        .select(`
          organizations!inner(id, name, is_pro, api_key, feature_flags)
        `)
        .eq("user_id", user.id);

      if (orgsError) {
        console.error("Error fetching organizations:", orgsError);
        throw orgsError;
      }

      if (!orgsData || orgsData.length === 0) {
        console.log("No organizations found");
        setOrganizations([]);
        setCurrentOrganization(null);
        setIsLoading(false);
        return;
      }

      const orgs = orgsData.map((item) => ({
        id: item.organizations.id,
        name: item.organizations.name,
        is_pro: item.organizations.is_pro || false,
        api_key: item.organizations.api_key || false,
        feature_flags: item.organizations.feature_flags || {},
      }));

      setOrganizations(orgs);

      // Set current organization based on JWT
      if (activeOrgId && orgs.length > 0) {
        const activeOrg = orgs.find((org) => org.id === activeOrgId);
        if (activeOrg) {
          setCurrentOrganization(activeOrg);
        } else {
          // JWT has an organization ID that user is no longer a member of

          // Set the first organization as active and update JWT
          setCurrentOrganization(orgs[0]);
          await switchOrganization(orgs[0].id);
        }
      } else if (orgs.length > 0) {
        // No active organization in JWT, set first one
        console.log(
          "No active organization in JWT, setting first one:",
          orgs[0]
        );
        setCurrentOrganization(orgs[0]);
        await switchOrganization(orgs[0].id);
      } else {
        // No organizations available
        setCurrentOrganization(null);
      }
    } catch (error) {
      console.error("Error loading organizations:", error);
      setOrganizations([]);
      setCurrentOrganization(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadOrganizations();
    }
  }, [user]);

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        setCurrentOrganization,
        organizations,
        loadOrganizations,
        isLoading,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

// Define the App component as a default export
export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <OrganizationProvider>
          <Suspense fallback={<LoadingScreen text="Loading application..." />}>
            <AppRoutes />
            <OrganizationPopupHandler />
          </Suspense>
          <Toaster />
        </OrganizationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
